import { useEffect, useState, useCallback } from 'react'
import * as io from 'socket.io-client'

// Địa chỉ WebSocket server của bạn
const SOCKET_SERVER_URL = 'wss://admin.alowallet.net/' // Port của laravel-echo-server

// Tên channel và event từ backend
// const PUBLIC_CHANNEL_NAME = 'notification'
const EVENT_NAME_ON_PUBLIC_CHANNEL = 'blockchain.notification.created' // Phải khớp với broadcastAs()

// Đối với laravel-echo-server, khi subscribe vào channel
// và lắng nghe event, tên event thực tế mà socket.io-client nhận được
// thường có dạng: `channelName:eventName` nếu không có config đặc biệt.
// Tuy nhiên, laravel-echo-server có thể emit event với tên là eventName
// sau khi đã subscribe thành công. Chúng ta sẽ thử lắng nghe trực tiếp eventName.

export const useSocket = (userId, channel = "notification", eventName = EVENT_NAME_ON_PUBLIC_CHANNEL) => {
	// Tên channel public mà bạn muốn subscribe

	const [socket, setSocket] = useState<io.Socket | null>(null)
	const [isConnected, setIsConnected] = useState(false)
	const [lastMessage, setLastMessage] = useState<any>(null)

	useEffect(() => {
		if (!userId) return

		// Tạo kết nối WebSocket với server
		// Với public channel, thông tin 'auth' có thể không cần thiết
		// trừ khi laravel-echo-server của bạn cấu hình 'clients' và yêu cầu appId/key cho mọi kết nối.
		// Nếu server có cấu hình clients trong laravel-echo-server.json:
		// const socketConnection = io.connect(SOCKET_SERVER_URL, {
		//   query: {
		//     appId: 'your_app_id', // Lấy từ laravel-echo-server.json
		//     // key: 'your_app_key' // key thường không gửi qua query vì lý do bảo mật
		//   },
		//   transports: ['websocket'],
		// });
		// Nếu không có 'clients' hoặc muốn đơn giản:
		const PUBLIC_CHANNEL_NAME = `${channel}.${userId}`

		const socketConnection = io.connect(SOCKET_SERVER_URL, {
			transports: ['websocket'],
			// auth: { // Thử bỏ qua auth này nếu không cần thiết cho public channel
			//   token: `Bearer ${token}`,
			//   // app_key: 'local', // laravel-echo-server không dùng app_key theo cách này
			// }
		})

		socketConnection.on('connect', () => {
			console.log('Socket.IO: Connected to WebSocket server.')
			setIsConnected(true)

			// 1. GỬI YÊU CẦU "SUBSCRIBE" ĐẾN PUBLIC CHANNEL
			// laravel-echo-server mong đợi một event 'subscribe' với payload chứa channel name
			const subscriptionPayload = {
				channel: PUBLIC_CHANNEL_NAME,
				// auth: {} // Đối với public channel, auth thường là object rỗng
			}
			socketConnection.emit('subscribe', subscriptionPayload)
			console.log(`Socket.IO: Subscribing to channel: ${PUBLIC_CHANNEL_NAME}`)

			// Server sẽ gửi lại event xác nhận subscribe (tên event có thể khác nhau tùy config server)
			// Ví dụ: 'subscribed' hoặc 'pusher_internal:subscription_succeeded'
			// Bạn có thể lắng nghe những event này để biết khi nào subscribe thành công.
			// socketConnection.on('pusher_internal:subscription_succeeded', (data) => {
			//    if (data.channel === PUBLIC_CHANNEL_NAME) {
			//        console.log(`Socket.IO: Successfully subscribed to channel: ${PUBLIC_CHANNEL_NAME}`);
			//    }
			// });

			// 2. LẮNG NGHE EVENT TRÊN CHANNEL ĐÃ SUBSCRIBED
			// Sau khi subscribe thành công, laravel-echo-server sẽ emit các event
			// với tên event là giá trị của `broadcastAs()` (hoặc tên class event).
			socketConnection.on(eventName, (event, data: any) => {
				console.log(`Socket.IO: Received event "${eventName}" on channel "${PUBLIC_CHANNEL_NAME}":`, data)
				setLastMessage(data)
				// Xử lý dữ liệu nhận được, ví dụ: cập nhật UI, dispatch action, ...
				// if (data.order_id && data.status) {
				//   alert(`Order ${data.order_id} status updated to: ${data.status}`);
				// }
			})

			// socketConnection.onAny((eventName, ...args) => {
			// 	console.log(`Socket.IO DEBUG - Received any event: ${eventName}`, args)
			// })

			// Một số phiên bản/cấu hình của echo server có thể emit event với prefix là tên channel
			// ví dụ: `orders:order.status.updated` hoặc `channel:event`
			// socketConnection.on(`${PUBLIC_CHANNEL_NAME}:${EVENT_NAME_ON_PUBLIC_CHANNEL}`, (data: any) => {
			//   console.log(`Socket.IO (prefixed): Received event "${PUBLIC_CHANNEL_NAME}:${EVENT_NAME_ON_PUBLIC_CHANNEL}":`, data);
			//   setLastMessage(data);
			// });
		})

		socketConnection.on('connect_error', (err) => {
			console.error('Socket.IO: Connection Error!', err)
			setIsConnected(false)
		})

		socketConnection.on('disconnect', (reason) => {
			console.log('Socket.IO: Disconnected from WebSocket server.', reason)
			setIsConnected(false)
		})

		// Bắt các event lỗi chung từ socket
		socketConnection.on('error', (error) => {
			console.error('Socket.IO: General error:', error)
		})

		setSocket(socketConnection)

		// Cleanup: Ngắt kết nối khi component unmount
		return () => {
			if (socketConnection) {
				// Có thể cần unsubscribe khỏi channel trước khi disconnect
				// socketConnection.emit('unsubscribe', { channel: PUBLIC_CHANNEL_NAME });
				console.log('Socket.IO: Disconnecting...')
				socketConnection.disconnect()
			}
		}
	}, [channel, eventName, userId]) // Chạy một lần khi component mount

	// Gửi dữ liệu tới server (nếu cần thiết, ít dùng với broadcasting)
	const sendMessage = useCallback(
		(event: string, data: any) => {
			if (socket && isConnected) {
				socket.emit(event, data)
				console.log(`Socket.IO: Sent message: ${event}`, data)
			} else {
				console.error('Socket.IO: Socket is not connected or not ready to send message.')
			}
		},
		[socket, isConnected]
	)

	return { socket, isConnected, lastMessage, sendMessage }
}
